// **React Imports
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'

// **Custom Component
import ControlledTextField from '../../../../components/controlled-textfield'
import CustomDateTimePicker from '../../../../components/date-time-picker'
import DemoGuard from '../../../../components/demo-guard'
import CustomDialog from '../../../../components/dialog'
import Icon from '../../../../components/icon'
import ResumeUpload from '../../../../components/resume-upload'
import VirtualizedAutocomplete from '../../../../components/virtualized-autocomplete'

// ** Third Party Imports
import { yupResolver } from '@hookform/resolvers/yup'
import { Controller, useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import * as yup from 'yup'

// ** MUI Imports
import { Call, Mail, Person } from '@mui/icons-material'
import {
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Radio,
  styled,
  Typography,
  useTheme
} from '@mui/material'

// ** Utils Imports
import SampleData from '../../../../assets/csv-files/Invite_Candidate.csv'
import { isEnableData } from '../../../../utils/constant'
import {
  hexToRGBA,
  rgbaToHexWithoutOpacity,
  showErrorToast,
  validateZeroByteFile
} from '../../../../utils/utils'
import {
  emailValidation,
  phoneNumberValidation,
  validateName
} from '../../../../utils/validations'

// **API Imoports
import { inviteCandidate } from '../../../../axios/api/candidate'
import { getContestAssessmentList } from '../../../../axios/api/chatbot'
import { inviteAssessmentCandidate } from '../../../../axios/api/contest'

// **Style import
import './InviteCandidate.scss'

// **Styled Component
const FormContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  padding: theme.spacing(4)
}))

const INVITE_CANDIDATE_TYPE = [
  {
    value: 'individual',
    label: 'Individual',
    icon: 'fluent:person-mail-20-filled'
  },
  {
    value: 'bulk',
    label: 'Bulk',
    icon: 'fluent:person-note-24-filled'
  }
]

const InviteCandidate = ({
  open,
  title,
  contestId,
  inputData,
  handleClose,
  fetchCandidates,
  handleCancelClick,
  handleInviteClick,
  isAssessment = false,
  isFromChatbot = false,
  handleUploadInviteCandidateCSV = () => {}
}) => {
  const theme = useTheme()
  const { contestId: paramsContestId } = useParams()

  //State
  const [resume, setResume] = useState({ file: null, error: '' })
  const [loading, setLoading] = useState(false)
  const [selectedTab, setSelectedTab] = useState('individual')
  const [selectedFile, setSelectedFile] = useState(null)
  const [bulkUploadError, setBulkUploadError] = useState(null)
  const [windowInnerHeight, setWindowInnerHeight] = useState(0)
  const [assessmentOptions, setAssessmentOptions] = useState([])
  const [loadingOptions, setLoadingOptions] = useState(false)

  // variable
  const defaultValues = useMemo(
    () => ({
      name: '',
      phoneNumber: '',
      email: '',
      isEnableNow: isAssessment,
      startTime: '',
      contestId: null
    }),
    [isAssessment]
  )

  const schema = useMemo(() => {
    return yup.object().shape({
      name: validateName('Full name'),
      phoneNumber: phoneNumberValidation,
      email: emailValidation,
      isEnableNow: yup.boolean(),
      contestId: isFromChatbot
        ? yup.mixed().required('Contest or Assessment is required')
        : yup.string().nullable(),
      startTime: isAssessment
        ? yup.mixed().when('isEnableNow', {
            is: false,
            then: (schema) =>
              schema
                .test(
                  'startTime-validation',
                  'Assessment start time is required',
                  (value) => value != null && value !== ''
                )
                .test(
                  'startTime-valid-date',
                  'Enter valid date and time',
                  (value) => {
                    if (!value) return true // Skip if empty; first test handles required
                    return !isNaN(new Date(value).getTime())
                  }
                ),
            otherwise: (schema) => schema.notRequired()
          })
        : yup.string().notRequired()
    })
  }, [isAssessment, isFromChatbot])

  // hook
  const fileInputRef = useRef(null)
  const { general = {} } = useSelector(
    (state) => state.contest.selectedContest || {}
  )

  const {
    control,
    reset,
    handleSubmit,
    setValue,
    trigger,
    formState: { errors }
  } = useForm({
    defaultValues,
    mode: 'onBlur',
    resolver: yupResolver(schema)
  })

  //handlers

  const handleTabChange = useCallback(
    (event) => {
      const tabValue = event.target.getAttribute('value')
      if (tabValue !== selectedTab) {
        setSelectedTab(tabValue)
      }
      reset()
      setSelectedFile(null)
      setBulkUploadError(null)
    },
    [selectedTab, reset]
  )

  const onSubmit = useCallback(
    async (data) => {
      let response

      const payload = {
        ...data,
        startTime: data?.startTime
          ? new Date(data.startTime).toISOString()
          : null,
        phoneNumber:
          data?.phoneNumber?.trim() !== '' ? data?.phoneNumber?.trim() : null
      }

      if (typeof handleInviteClick === 'function') {
        handleInviteClick(payload)
        return
      }

      setLoading(true)

      if (isAssessment) {
        const formData = new FormData()
        formData.append('resume', resume?.file)

        Object.keys(payload).forEach((key) => {
          if (key === 'isEnableNow') {
            formData.append(key, payload?.[key] || false)
          } else if (key !== 'contestId') {
            formData.append(key, payload?.[key] || '')
          }
        })

        formData.append('assessmentId', contestId)

        response = await inviteAssessmentCandidate(formData)
      } else {
        const payload = {
          ...data,
          contestId,
          startTime: null,
          phoneNumber:
            data?.phoneNumber?.trim() !== '' ? data?.phoneNumber?.trim() : null
        }
        response = await inviteCandidate(payload, isAssessment)
      }

      setLoading(false)
      if (response?.isError) {
        showErrorToast(response)
      } else {
        handleClose()
        fetchCandidates()
        toast.success('Candidate Invited Successfully')
      }
    },
    [
      contestId,
      resume,
      handleClose,
      fetchCandidates,
      isAssessment,
      handleInviteClick
    ]
  )

  const handleResumeChange = useCallback((value) => {
    let error = ''
    if (value) {
      const fileExtension = value.name.split('.').pop().toLowerCase()
      const maxSizeInBytes = 2 * 1024 * 1024
      const allowedExtensions = ['.pdf', '.docx', '.doc']

      if (!allowedExtensions.includes(`.${fileExtension}`)) {
        error = `Invalid file format. Only ${allowedExtensions?.join(
          ', '
        )} are allowed.`
      } else if (value.size > maxSizeInBytes) {
        error = 'File size exceeds 2MB limit.'
      } else {
        error = ''
      }
    }

    setResume({ file: value, error })
  }, [])

  // File upload

  const handleCSVupload = useCallback(() => {
    if (selectedFile) {
      handleUploadInviteCandidateCSV(selectedFile)
    } else {
      setBulkUploadError('Add csv, xlsx and xls file to invite candidates.')
    }
  }, [selectedFile, handleUploadInviteCandidateCSV])

  const handleFileChange = useCallback(({ target }) => {
    const file = target.files?.[0]

    if (validateZeroByteFile(file)) {
      target.value = null
      return
    }

    const fileExtension = file?.name?.split('.')?.slice(-1)?.[0]
    const validExtensions = ['csv', 'xlsx', 'xls']

    if (validExtensions.includes(fileExtension)) {
      setBulkUploadError(null)
      setSelectedFile(file)
    } else {
      setBulkUploadError('Allowed file extension are csv, xlsx and xls')
    }

    target.value = null
  }, [])

  const handleDownloadSampleData = useCallback(() => {
    const link = document.createElement('a')
    link.href = SampleData
    link.download = 'Invite_Candidate.csv' // Set the default file name for the download
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }, [])

  const fetchAssessmentOptions = useCallback(async () => {
    setLoadingOptions(true)
    const res = await getContestAssessmentList()
    setLoadingOptions(false)
    if (res?.isError) {
      setAssessmentOptions([])
    } else {
      setAssessmentOptions(res)
      if (paramsContestId) {
        const findContest = res?.find(
          (item) => item?.assessmentId === Number(paramsContestId)
        )
        if (findContest) {
          setValue('contestId', findContest)
        }
      }
    }
  }, [paramsContestId, setValue])

  // Dialog Min height

  const dialogMinHeight = useMemo(() => {
    if (isAssessment) {
      return windowInnerHeight < 720 ? '90%' : '670px'
    } else {
      return isFromChatbot ? 'auto' : windowInnerHeight < 420 ? '90%' : '384px'
    }
  }, [windowInnerHeight, isAssessment, isFromChatbot])

  //useEffect
  useEffect(() => {
    if (!open) {
      reset()
      setSelectedFile(null)
      setBulkUploadError(null)
      setResume({ file: null, error: '' })
      setSelectedTab('individual')
    }
  }, [open, reset])

  useEffect(() => {
    if (open && inputData) {
      setValue('name', inputData?.name)
      setValue('email', inputData?.email)
      setValue('phoneNumber', inputData?.phoneNumber)
    }
  }, [inputData, open, setValue])

  useEffect(() => {
    if (isFromChatbot && open) {
      fetchAssessmentOptions()
    }
  }, [open, isFromChatbot, fetchAssessmentOptions])

  // effect to prefill the contest start time as candidate start time
  useEffect(() => {
    if (general?.startTime && open) {
      const candidateStartTime = new Date(general?.startTime)

      if (candidateStartTime >= new Date()) {
        setValue('startTime', candidateStartTime)
      }
    }
  }, [general?.startTime, trigger, setValue, open])

  useEffect(() => {
    const handleResize = () => setWindowInnerHeight(window.innerHeight)

    window.addEventListener('resize', handleResize)

    handleResize()

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  return (
    <CustomDialog
      open={open}
      title={title}
      handleClose={handleClose}
      outsideClikClose={false}
      dialogPaperStyle={{
        minHeight: dialogMinHeight,
        '& .MuiDialogContent-root': {
          padding: '0 !important',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }
      }}
    >
      <Box className="invite-candidate-dialog">
        {!isFromChatbot && (
          <Box className="invite-candidate-dialog-tabs">
            {INVITE_CANDIDATE_TYPE.map((tab, index) => {
              return (
                <Box
                  value={tab.value}
                  key={`candidate-tab-${index}`}
                  className={`tab ${
                    tab.value === selectedTab ? 'active-tab' : ''
                  }`}
                  onClick={handleTabChange}
                  sx={{
                    ...(tab.value === selectedTab
                      ? {
                          backgroundColor: `${rgbaToHexWithoutOpacity(
                            ...hexToRGBA(theme.palette.primary.main, 0.2, true)
                          )}`
                        }
                      : {})
                  }}
                >
                  {tab.label}
                </Box>
              )
            })}
          </Box>
        )}

        <FormContainer className="invite-candidate-form-container">
          {selectedTab === 'individual' ? (
            <form
              noValidate
              autoComplete="off"
              onSubmit={handleSubmit(onSubmit)}
            >
              <Box mb={3}>
                <ControlledTextField
                  fullWidth
                  name={'name'}
                  control={control}
                  label={'Full Name'}
                  required
                  error={Boolean(errors.name)}
                  {...(errors.name && {
                    helperText: errors.name.message
                  })}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Person />
                      </InputAdornment>
                    )
                  }}
                />
              </Box>

              <Box mb={3}>
                <ControlledTextField
                  fullWidth
                  type="text"
                  name={'phoneNumber'}
                  control={control}
                  label={'Phone Number'}
                  error={Boolean(errors.phoneNumber)}
                  {...(errors.phoneNumber && {
                    helperText: errors.phoneNumber.message
                  })}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Call />
                      </InputAdornment>
                    )
                  }}
                />
              </Box>

              <Box mb={3}>
                <ControlledTextField
                  fullWidth
                  name={'email'}
                  required
                  control={control}
                  label={'Email'}
                  error={Boolean(errors.email)}
                  {...(errors.email && {
                    helperText: errors.email.message
                  })}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Mail />
                      </InputAdornment>
                    )
                  }}
                />
              </Box>

              {isFromChatbot && (
                <Box mb={3}>
                  <VirtualizedAutocomplete
                    name="contestId"
                    control={control}
                    label="Contest / Assessment"
                    required
                    options={assessmentOptions}
                    autoCompleteProps={{
                      getOptionLabel: (option) => option?.assessmentName || '',
                      isOptionEqualToValue: (option, value) =>
                        option?.assessmentId === value?.assessmentId,
                      loading: loadingOptions,
                      renderOption: (props, option) => (
                        <li
                          {...props}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            width: '100%',
                            alignItems: 'center'
                          }}
                        >
                          <Box>
                            <Typography variant="body1">
                              {option.assessmentName}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {option.isAssessment ? 'Assessment' : 'Contest'}
                            </Typography>
                          </Box>
                          <Icon
                            icon="mdi:eye-outline"
                            fontSize={20}
                            style={{
                              cursor: 'pointer',
                              color: 'var(--primary-main)'
                            }}
                            onClick={(e) => {
                              e.stopPropagation()
                              window.open(
                                `/${option.isAssessment ? 'assessments' : 'contests'}/${option.assessmentId}/edit?active=general`,
                                '_blank'
                              )
                            }}
                          />
                        </li>
                      )
                    }}
                    error={Boolean(errors.contestId)}
                    helperText={errors.contestId?.message}
                  />
                </Box>
              )}

              {isAssessment && (
                <Controller
                  name={'isEnableNow'}
                  control={control}
                  render={({ field: { onChange, value, ...rest } }) => {
                    return (
                      <>
                        <Box mb={3} sx={{ display: 'flex', gap: '10px' }}>
                          {isEnableData?.map((item) => (
                            <Box
                              key={item?.value}
                              sx={{
                                gap: '9px',
                                display: 'flex',
                                cursor: 'pointer',
                                alignItems: 'center'
                              }}
                              onClick={() => {
                                onChange({ target: { value: item?.value } })
                                setValue('startTime', '')
                              }}
                            >
                              <Radio
                                checked={value === item?.value}
                                sx={{ padding: 0 }}
                              />
                              <Typography>{item?.label}</Typography>
                            </Box>
                          ))}
                        </Box>
                        {!value && (
                          <Box mb={3}>
                            <CustomDateTimePicker
                              required
                              name="startTime"
                              control={control}
                              error={Boolean(errors.startTime)}
                              {...(errors.startTime && {
                                helperText: errors.startTime.message
                              })}
                              label={'Assessment Start Time'}
                            />
                          </Box>
                        )}
                      </>
                    )
                  }}
                />
              )}

              {isAssessment && (
                <Box
                  mb={3}
                  sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}
                >
                  <ResumeUpload
                    label={'Resume'}
                    value={resume.file}
                    error={resume.error}
                    handleChange={handleResumeChange}
                  />
                </Box>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                {isFromChatbot && (
                  <Button
                    variant="outlined"
                    onClick={handleCancelClick}
                    sx={{ flex: 1 }}
                  >
                    Cancel
                  </Button>
                )}
                <DemoGuard>
                  <Button
                    className="invite-candidate-btn"
                    fullWidth
                    type="submit"
                    variant="contained"
                    disabled={loading}
                    sx={{ flex: 1 }}
                  >
                    {loading ? <CircularProgress size={22} /> : 'Invite'}
                  </Button>
                </DemoGuard>
              </Box>
            </form>
          ) : (
            <Box className="bulk-invite-candidates">
              <Box className="card-header">
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleDownloadSampleData}
                >
                  <Icon
                    icon={'mdi:file-download'}
                    fontSize={20}
                    style={{ color: 'var(--primary-main)' }}
                  />
                  Download Sample Data
                </Button>
              </Box>
              <Box className="upload-card">
                <Box
                  className={`bordered-card ${bulkUploadError ? 'error' : ''}`}
                  onClick={(e) => {
                    fileInputRef?.current?.click()
                  }}
                >
                  <Icon
                    icon={'material-symbols:upload'}
                    nonHoverBoxStyle={{ width: 'max-content' }}
                    style={{ fontSize: '2rem', color: 'var(--primary-main)' }}
                  />
                  <Typography variant="body1" sx={{ mt: 1 }}>
                    Click to select a file
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {`Supported formats: .csv, .xlsx or .xls`}
                  </Typography>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                  />
                  {selectedFile && (
                    <Box sx={{ gap: '8px', display: 'flex' }}>
                      <Typography variant="body2" sx={{ mt: 2 }}>
                        Selected: <strong>{selectedFile?.name}</strong>
                      </Typography>
                      <Icon
                        hoverEffect
                        icon={'carbon:close-outline'}
                        onClick={(e) => {
                          e.stopPropagation()
                          e.preventDefault()
                          setSelectedFile(null)
                        }}
                      />
                    </Box>
                  )}
                  {bulkUploadError && (
                    <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                      {bulkUploadError}
                    </Typography>
                  )}
                </Box>
              </Box>
              <DemoGuard>
                <Button
                  className="invite-candidate-btn"
                  fullWidth
                  variant="contained"
                  onClick={handleCSVupload}
                >
                  Upload
                </Button>
              </DemoGuard>
            </Box>
          )}
        </FormContainer>
      </Box>
    </CustomDialog>
  )
}

export default memo(InviteCandidate)
